import { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useParams } from "react-router-dom";
import TicketVenta from "./TicketVenta";

// Componentes modularizados
import ProductSearchPanel from './carrito/ProductSearchPanel';
import CartTable from './carrito/CartTable';
import CartSummary from './carrito/CartSummary';
import PaymentModeSelector from './carrito/PaymentModeSelector';
import WholesalePriceSelector from './carrito/WholesalePriceSelector';
import NavigationButtons from './carrito/NavigationButtons';
// import HighlightedProduct from './carrito/HighlightedProduct';
import UserInfo from './carrito/UserInfo';
import SuccessNotification from './carrito/SuccessNotification';

// Hooks personalizados
import useNavigationProtection from '../hooks/useNavigationProtection';
import useCartLogic from '../hooks/useCartLogic';

const CompCarrito = () => {
  const [productsData, setProductsData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedWholesalePrice, setSelectedWholesalePrice] = useState('precio_mayorista_a');
  const { token, fullname, userId } = useAuth();
  const { tipo } = useParams();
  const [discount, setDiscount] = useState(0); // Estado para el descuento
  const [clientes, setClientes] = useState([]);
  const [clienteSeleccionado, setClienteSeleccionado] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('efectivo'); // Predeterminado: efectivo
  const [paymentData, setPaymentData] = useState({
    mode: 'simple',
    paymentMethod: 'efectivo',
    payments: [],
    isValid: true
  });
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [lastSaleData, setLastSaleData] = useState(null); // Datos de la última venta para el ticket
  const [selectedProductIndex, setSelectedProductIndex] = useState(-1); // Índice del producto seleccionado por teclado
  const caja = localStorage.getItem('cajaAbiertaId');

  // Usar hooks personalizados
  const {
    cart,
    setCart,
    getPrice,
    getDiscountedPrice,
    addToCart,
    removeFromCart,
    increaseQuantity,
    decreaseQuantity,
    handleQuantityChange,
    calculateTotal,
    searchInputRef
  } = useCartLogic(tipo, selectedWholesalePrice);

  // Protección de navegación
  useNavigationProtection(cart, setCart);

  // Resetear índice seleccionado cuando cambia el término de búsqueda
  useEffect(() => {
    setSelectedProductIndex(-1);
  }, [searchTerm]);

  const printTicket = () => {
    // Decide qué datos imprimir: si hay carrito, imprime el actual; si no, la última venta
    const hasCart = cart && cart.length > 0;
    const ticketData = hasCart
      ? {
          cart,
          total: calculateTotal(),
          discount,
          totalWithDiscount: calculateTotalWithDiscount(),
          paymentMethod,
          selectedWholesalePrice,
          tipo,
          cliente: clienteInfo,
          fullname,
        }
      : (lastSaleData ? { ...lastSaleData, fullname } : null);

    if (!ticketData) return;

    // Renderiza el ticket en un div temporal
    const tempDiv = document.createElement('div');
    const tempRoot = document.createElement('div');
    tempDiv.appendChild(tempRoot);
    // Renderiza el componente TicketVenta en el tempRoot
    import('react-dom').then(ReactDOM => {
      ReactDOM.render(
        <TicketVenta {...ticketData} />,
        tempRoot
      );

      setTimeout(() => {
        const printWindow = window.open('', '_blank', 'width=400,height=600');
        printWindow.document.write(`
          <html>
            <head>
              <title>Ticket de Venta</title>
              <style>
                @media print {
                  @page { size: 8cm auto; margin: 0; }
                  body { margin: 0; padding: 0; }
                }
                body {
                  font-family: monospace;
                  width: 8cm;
                  margin: 0;
                  padding: 10px;
                  background: white;
                  color: black;
                  font-size: 13px;
                }
              </style>
            </head>
            <body>
              ${tempRoot.innerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.onload = () => {
          printWindow.print();
          printWindow.onafterprint = () => {
            printWindow.close();
          };
        };
      }, 100);
    });
  };

  // Cargar productos una sola vez al montar el componente
  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/productos/admin`, {
          headers: { "x-auth-token": token },
        });
        setProductsData(response.data.productos);
      } catch (error) {
        console.error("Error al cargar productos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [token]);
  
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return productsData; // Si no hay búsqueda, mostrar todos

    const term = searchTerm.toLowerCase().trim();

    return productsData.filter((product) => {
      const nameMatch = product.nombre_producto.toLowerCase().includes(term);

      // Busca dentro de `codigo_barras[]`, accediendo a `codigo`
      const barcodeMatch = product.codigo_barras?.some((item) =>
        item.codigo.toLowerCase().includes(term)
      );

      return nameMatch || barcodeMatch;
    });
  }, [searchTerm, productsData]);


  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const response = await axios.get(`${import.meta.env.VITE_API_URL}/clientes`, {
          headers: { "x-auth-token": token },
        });
        setClientes(response.data);
      } catch (error) {
        console.error("Error al cargar clientes:", error);
      }
    };
    fetchClientes();
  }, [token]);
  // Función para manejar la búsqueda por Plug asociado (cuando se presiona Enter)
  const handleSearchKeyDown = (e) => {
    if (filteredProducts.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedProductIndex(prev => 
        prev < filteredProducts.length - 1 ? prev + 1 : 0
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedProductIndex(prev => 
        prev > 0 ? prev - 1 : filteredProducts.length - 1
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (selectedProductIndex >= 0 && selectedProductIndex < filteredProducts.length) {
        // Añadir producto seleccionado por índice
        addToCart(filteredProducts[selectedProductIndex], caja, setSearchTerm);
        setSelectedProductIndex(-1); // Resetear selección
      } else if (filteredProducts.length === 1) {
        // Comportamiento original: si solo hay un producto, añadirlo
        addToCart(filteredProducts[0], caja, setSearchTerm);
      }
    } else if (e.key === "Escape") {
      setSelectedProductIndex(-1); // Limpiar selección
    }
  };

  const handleWholesalePriceChange = (e) => {
    setSelectedWholesalePrice(e.target.value);
  };

  const handlePaymentMethodChange = (method) => {
    setPaymentMethod(method);
    // Limpiar cliente seleccionado cuando no es cuenta corriente
    if (method !== 'cuentaCorriente') {
      setClienteSeleccionado(null);
    }
  };

  const handlePaymentDataChange = useCallback((data) => {
    setPaymentData(data);
    console.log('Payment data updated:', data);
  }, []);

  const handleCheckout = useCallback(async () => {
    const invalidProduct = cart.find((item) => !item.quantity || item.quantity <= 0);

    if (invalidProduct) {
      alert(`El producto "${invalidProduct.nombre_producto}" tiene una cantidad inválida. Corrígelo antes de continuar.`);
      return;
    }

    // Validar datos de pago
    if (!paymentData.isValid) {
      alert('Los datos de pago no son válidos. Por favor, revisa los montos y formas de pago.');
      return;
    }

    // El descuento es fijo, no porcentual
    const descuentoAplicado = parseFloat(discount) > 0 ? parseFloat(discount) : 0;
    const totalSinDescuento = calculateTotal();

    if (descuentoAplicado > totalSinDescuento) {
      alert("El descuento no puede ser mayor al total.");
      return;
    }

    const totalConDescuento = totalSinDescuento - descuentoAplicado;

    // Validar cliente para cuenta corriente
    const needsClient = paymentData.mode === 'simple' 
      ? paymentMethod === 'cuentaCorriente'
      : paymentData.payments?.some(p => p.forma_pago === 'cuentaCorriente');

    if (needsClient && !clienteSeleccionado) {
      alert('Debe seleccionar un cliente para pagos con cuenta corriente.');
      return;
    }

    setLoading(true);

    try {
      const basePayload = {
        productos: cart.map((item) => ({
          id: item.id,
          cantidad: item.quantity,
          precio: getDiscountedPrice(item),
          stock_unidades: item.stock_unidades,
        })),
        total: totalConDescuento,
        mayorista: tipo === 'mayorista' ? selectedWholesalePrice : false,
        userId,
        descuento: descuentoAplicado,
        id_cliente: clienteSeleccionado,
      };

      let payload;

      if (paymentData.mode === 'simple') {
        // Pago tradicional (compatibilidad hacia atrás)
        payload = {
          ...basePayload,
          detalles_pago: paymentMethod,
        };
      } else {
        // Pagos múltiples (nueva funcionalidad)
        payload = {
          ...basePayload,
          pagos_multiples: paymentData.payments.map(payment => ({
            forma_pago: payment.forma_pago,
            monto: parseFloat(payment.monto),
            referencia: payment.referencia || null,
            observaciones: payment.observaciones || null
          }))
        };
      }

      console.log('Sending payload:', payload);

      // Realizar la solicitud POST usando axios
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/ventas`,
        payload,
        {
          headers: {
            'x-auth-token': token,
          }
        }
      );

      if (response.status !== 201) {
        throw new Error('Error al realizar la venta');
      }

      // Guardar datos de la venta para el ticket antes de limpiar
      const clienteInfo = clienteSeleccionado ? clientes.find(c => c.id === parseInt(clienteSeleccionado)) : null;
      setLastSaleData({
        cart: [...cart],
        total: totalSinDescuento,
        discount: descuentoAplicado,
        totalWithDiscount: totalConDescuento,
        paymentMethod: paymentMethod,
        paymentData: {...paymentData},
        selectedWholesalePrice: selectedWholesalePrice,
        tipo: tipo,
        cliente: clienteInfo,
        fecha: new Date(),
        getPrice: getPrice // Función para calcular precios
      });

      // Limpiar formulario
      setCart([]);
      setDiscount('');
      setPaymentMethod('efectivo');
      setPaymentData({
        mode: 'simple',
        paymentMethod: 'efectivo',
        payments: [],
        isValid: true
      });
      setClienteSeleccionado(null);
      
      const successMessage = paymentData.mode === 'multiple' 
        ? `Venta realizada con éxito usando ${paymentData.payments.length} formas de pago`
        : 'Venta realizada con éxito';
      
      setSuccessMessage(successMessage);
      setShowSuccessNotification(true);

    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'Error al realizar la venta';
      alert(errorMessage);
      console.error('Error en checkout:', error);
    } finally {
      setLoading(false);
    }
  }, [cart, discount, calculateTotal, getDiscountedPrice, tipo, selectedWholesalePrice, userId, paymentData, paymentMethod, clienteSeleccionado, token, setCart, clientes, getPrice]);

  const handleDiscountChange = (event) => {
    let value = event.target.value.trim();
    if (value === "") {
      setDiscount("");
    } else {
      let parsedValue = parseFloat(value);
      if (!isNaN(parsedValue) && parsedValue >= 0) {
        setDiscount(parsedValue);
      }
    }
  };

  // Función para calcular el total con descuento fijo
  const calculateTotalWithDiscount = () => {
    const total = calculateTotal();
    const discountAmount = parseFloat(discount) || 0;
    // El descuento no puede ser mayor al total
    return total - Math.min(discountAmount, total);
  };

  // Obtener información completa del cliente seleccionado
  const clienteInfo = useMemo(() => {
    if (!clienteSeleccionado) return null;
    return clientes.find(cliente => cliente.id === parseInt(clienteSeleccionado));
  }, [clienteSeleccionado, clientes]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'F1') {
        e.preventDefault(); // Evita que se abra la ayuda del navegador
        handleCheckout();   // Llama a tu función de checkout
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleCheckout]); // Asegurate de que handleCheckout esté definido correctamente

  const getBgColor = () => {
    if (tipo === 'mayorista') {
      switch (selectedWholesalePrice) {
        case 'precio_mayorista_a':
          return 'bg-red-500';
        case 'precio_mayorista_b':
          return 'bg-green-300';
        case 'precio_mayorista_c':
          return 'bg-violet-300';
        default:
          return 'bg-violet-300';
      }
    }
    return 'bg-white';
  };
  return (
    <div className={`min-h-screen w-full ${getBgColor()}`}>
      
      <div className="container mx-auto p-2 md:p-4 max-w-7xl pb-24">
        {/* Cabecera rediseñada */}
        
        {/* Sticky de navegación a la derecha */}
        <div className="fixed top-0 right-0 z-50 flex flex-col items-end px-2 ">
          <NavigationButtons tipo={tipo} bigHistorial userFullname={fullname} />
          <UserInfo fullname={fullname} big />
        </div>

        {loading && <p>Cargando...</p>}

    

        {/* Buscador, producto destacado y lista de productos juntos y relacionados */}
        <ProductSearchPanel
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onSearchKeyDown={handleSearchKeyDown}
          inputRef={searchInputRef}
          cart={cart}
          getDiscountedPrice={getDiscountedPrice}
          filteredProducts={filteredProducts}
          onAddToCart={(product) => addToCart(product, caja, setSearchTerm)}
          getPrice={getPrice}
          selectedIndex={selectedProductIndex}
        />
    {tipo === 'mayorista' && (
          <WholesalePriceSelector
            selectedWholesalePrice={selectedWholesalePrice}
            onWholesalePriceChange={handleWholesalePriceChange}
          />
        )}
        <CartTable
          cart={cart}
          getDiscountedPrice={getDiscountedPrice}
          onQuantityChange={handleQuantityChange}
          onDecreaseQuantity={decreaseQuantity}
          onIncreaseQuantity={increaseQuantity}
          onRemoveFromCart={removeFromCart}
        />

        <CartSummary
          calculateTotal={calculateTotal}
          discount={discount}
          onDiscountChange={handleDiscountChange}
          calculateTotalWithDiscount={calculateTotalWithDiscount}
          loading={loading}
          onCheckout={handleCheckout}
          onPrintTicket={() => printTicket()}
        />
      </div>

      {/* El ticket oculto ya no es necesario, la impresión es dinámica */}

      <PaymentModeSelector
        total={calculateTotalWithDiscount()}
        paymentMethod={paymentMethod}
        onPaymentMethodChange={handlePaymentMethodChange}
        clientes={clientes}
        clienteSeleccionado={clienteSeleccionado}
        setClienteSeleccionado={setClienteSeleccionado}
        onPaymentDataChange={handlePaymentDataChange}
      />

   

      <SuccessNotification
        isVisible={showSuccessNotification}
        message={successMessage}
        paymentData={paymentData}
        onClose={() => setShowSuccessNotification(false)}
        onPrint={printTicket}
      />
    </div>
  );
};

export default CompCarrito;
